import React, { useMemo, useState } from 'react'
import { FaSearch } from 'react-icons/fa'
import { Banner, ChipSelect, ConfirmSheet, Field, FoBtn, Pill, QtyStepper, ScreenShell, Sheet, TextArea } from './fieldUi'
import { LinesTable } from './shared'
import { useField } from './fieldContext'
import { storeInfo, storeMap } from '../store'
import { PRODUCTS, PRODUCT_CATEGORIES } from '../demoData'
import { addDays, fmtDate, money2, weekday } from '../FieldOpsKit'

/**
 * Sales Order builder for the phone (rep, from a visit). Usual items first, a search, big steppers, price floor per
 * product, and a review step. Offline: it saves on the device and the SO number is assigned when the phone reconnects.
 * Demo: nothing is sent to Sage.
 */
export default function OrderBuilder({ visitId }) {
    const f = useField()
    const { state, nav, actor, online } = f
    const visit = state.sales.visits.filter((v) => v.id === visitId)[0]
    const store = visit ? storeMap(state.sales)[visit.storeId] : null
    const info = useMemo(() => (store ? storeInfo(state.sales, store.id, f.vIdx, f.oIdx) : null), [state.sales, store ? store.id : '']) // eslint-disable-line react-hooks/exhaustive-deps
    const [qty, setQty] = useState({})
    const [price, setPrice] = useState({})
    const [editing, setEditing] = useState('')
    const [q, setQ] = useState('')
    const [cat, setCat] = useState('Usual')
    const [review, setReview] = useState(false)
    const [notes, setNotes] = useState('')
    const [deliver, setDeliver] = useState('')
    const [discard, setDiscard] = useState(false)
    const [tried, setTried] = useState(false)

    if (!visit || !store) return <ScreenShell title="Order" onBack={nav.pop}><Banner tone="warn">This visit is not available.</Banner></ScreenShell>

    // quantities of the last order of this store → "Last time" hints and the "Usual" filter
    const last = {}
    const usual = {}
    ;(info ? info.orders : []).slice(0, 3).forEach((o, i) => o.lines.forEach((l) => { usual[l.sku] = true; if (i === 0) last[l.sku] = l.qty }))
    const unit = (p) => (price[p.sku] !== undefined && price[p.sku] !== '' ? Number(price[p.sku]) : p.price)
    const cart = PRODUCTS.filter((p) => Number(qty[p.sku]) > 0).map((p) => ({ sku: p.sku, name: p.name, qty: Number(qty[p.sku]), price: unit(p), min: p.min }))
    const total = cart.reduce((a, l) => a + l.qty * l.price, 0)
    const units = cart.reduce((a, l) => a + l.qty, 0)
    const low = cart.filter((l) => l.price < l.min)
    const cats = ['Usual'].concat(PRODUCT_CATEGORIES).concat(['All'])
    const shown = PRODUCTS.filter((p) => {
        if (q.trim()) return (p.name + ' ' + p.sku).toLowerCase().indexOf(q.trim().toLowerCase()) >= 0
        if (cat === 'All') return true
        if (cat === 'Usual') return !!usual[p.sku] || Number(qty[p.sku]) > 0
        return p.cat === cat
    })
    const showUsualEmpty = cat === 'Usual' && !q.trim() && shown.length === 0

    const nextDays = []
    let d = f.date
    for (let i = 0; i < 4; i++) { d = addDays(d, 1); if (weekday(d) !== 0 && weekday(d) !== 6) nextDays.push(d) }

    const save = () => {
        setTried(true)
        if (cart.length === 0 || low.length > 0 || store.hold) return
        f.send({ type: 'ORDER_CREATE', storeId: store.id, rep: actor.rep, lines: cart.map((l) => ({ sku: l.sku, name: l.name, qty: l.qty, price: l.price })), visitId: visit.id, notes: notes, deliverOn: deliver })
        f.toast(online ? 'Order saved for ' + store.name + '.' : 'Order saved on this phone. It will be sent when you are online.', online ? 'ok' : 'warn')
        nav.pop()
    }
    const back = () => { if (cart.length) setDiscard(true); else nav.pop() }

    return (
        <ScreenShell title="New Sales Order" subtitle={store.name} onBack={back}
            footer={<React.Fragment>
                <div className="fo-cartbar"><span>{cart.length} item{cart.length === 1 ? '' : 's'} · {units} units</span><strong>{money2(total)}</strong></div>
                <FoBtn kind="primary" big disabled={cart.length === 0} onClick={() => setReview(true)}>Review</FoBtn>
            </React.Fragment>}>
            {store.hold ? <Banner tone="danger" title="Credit hold.">This store has a past due balance. You cannot save an order until the office clears it.</Banner> : null}
            <div className="fo-searchbox"><FaSearch /><input className="fo-input" placeholder="Search product or code" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search product" /></div>
            {!q.trim() ? <div className="fo-catscroll"><ChipSelect options={cats} value={cat} onChange={(v) => setCat(v || 'All')} /></div> : null}
            {showUsualEmpty ? <Banner tone="info">No previous orders for this store yet. Pick a category or search.</Banner> : null}
            <div className="fo-products">
                {shown.map((p) => {
                    const n = Number(qty[p.sku]) || 0
                    const isLow = n > 0 && unit(p) < p.min
                    return (
                        <div key={p.sku} className={'fo-product' + (n > 0 ? ' is-on' : '') + (p.stock === 0 ? ' is-out' : '')}>
                            <div className="fo-product__main">
                                <strong>{p.name}</strong>
                                <span>{p.sku} · {p.cat}</span>
                                <div className="fo-product__tags">
                                    {p.stock === 0 ? <Pill tone="red">Out of stock</Pill> : p.stock < 40 ? <Pill tone="amber">Only {p.stock} left</Pill> : null}
                                    {last[p.sku] ? (n === 0 && p.stock > 0 ? <button type="button" className="fo-pill fo-pill--blue fo-pill--btn" onClick={() => setQty({ ...qty, [p.sku]: Math.min(last[p.sku], p.stock) })}>+ Same as last time ({last[p.sku]})</button> : <Pill tone="gray">Last time {last[p.sku]}</Pill>) : null}
                                </div>
                                <button type="button" className={'fo-price' + (isLow ? ' is-bad' : '')} onClick={() => setEditing(editing === p.sku ? '' : p.sku)}>{money2(unit(p))} <small>{price[p.sku] !== undefined ? 'edited' : 'edit price'}</small></button>
                                {editing === p.sku ? (
                                    <div className="fo-priceedit">
                                        <input className="fo-input" type="number" inputMode="decimal" step="0.05" min="0" value={price[p.sku] === undefined ? p.price : price[p.sku]} onChange={(e) => setPrice({ ...price, [p.sku]: e.target.value })} aria-label={'Price ' + p.name} />
                                        <span>Minimum {money2(p.min)}</span>
                                    </div>
                                ) : null}
                                {isLow ? <div className="fo-field__error">Below the minimum price ({money2(p.min)}).</div> : null}
                            </div>
                            <QtyStepper value={n} onChange={(v) => setQty({ ...qty, [p.sku]: v })} label={p.name} max={p.stock === 0 ? 0 : undefined} />
                        </div>
                    )
                })}
            </div>

            <Sheet show={review} onClose={() => setReview(false)} title="Review the order" subtitle={store.name} tall
                footer={<React.Fragment><FoBtn kind="ghost" onClick={() => setReview(false)}>Keep editing</FoBtn><FoBtn kind="success" onClick={save}>{online ? 'Save order' : 'Save on phone'}</FoBtn></React.Fragment>}>
                {tried && low.length > 0 ? <Banner tone="danger" title="Some prices are below the minimum.">{low.map((l) => l.name).join(', ')}</Banner> : null}
                {!online ? <Banner tone="warn">You are offline. The order is stored on this phone and gets its SO number when it is sent.</Banner> : null}
                <LinesTable lines={cart} />
                <Field label="Requested delivery"><div className="fo-chips"><button type="button" className={'fo-chip' + (deliver === '' ? ' is-on' : '')} onClick={() => setDeliver('')}>Office decides</button>{nextDays.map((x) => <button key={x} type="button" className={'fo-chip' + (deliver === x ? ' is-on' : '')} onClick={() => setDeliver(x)}>{fmtDate(x)}</button>)}</div></Field>
                <Field label="Notes for the office (PO number, special price, instructions)"><TextArea value={notes} onChange={setNotes} rows={3} placeholder="Optional" /></Field>
            </Sheet>

            <ConfirmSheet show={discard} title="Discard this order?" text="You added products that are not saved yet." confirmLabel="Discard order" tone="danger" noteLabel={null} onClose={() => setDiscard(false)} onConfirm={() => { setDiscard(false); nav.pop() }} />
        </ScreenShell>
    )
}
